import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  fetchSetupOnboardingSignals,
  fetchSetupOnboardingState,
  buildSetupOnboardingChecklist,
  getSetupOnboardingProgress
} from '../utils/setupOnboarding';
import {
  Sparkles, CheckCircle2, Circle,
  ArrowLeft, ShoppingBag,
  Banknote, Users, Building, CreditCard, Receipt,
  ArrowUpRight, Globe, PackagePlus, UserCheck, Layers, Landmark, ArrowLeftRight,
  TrendingUp, BarChart3, ShieldCheck, Rocket, PieChart, Printer, RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { startSetupTour } from '../utils/setupOnboardingTours';
import { getOnboardingTour } from '../utils/onboardingEngine';

const STEP_ICONS = {
  'business-data': Building,
  'load-catalog': ShoppingBag,
  'payment-methods': CreditCard,
  'open-cash': Banknote,
  'first-sale': CreditCard,
  'first-purchase': ShoppingBag,
  'review-profitability': TrendingUp,
  'explore-reports': BarChart3,
  'load-recipes': Receipt,
  'configure-price-lists': Layers,
  'create-first-combo': PackagePlus,
  'setup-client-credit': UserCheck,
  'publish-web-store': Globe,
  'setup-treasury-accounts': Landmark,
  'invite-team': Users,
  'configure-billing': Receipt,
  'setup-multi-site-stock': ArrowLeftRight,
  'print-barcode-labels': Printer,
  'stock-control': Layers,
  'load-suppliers': PackagePlus,
  'product-costs': TrendingUp,
  'product-categories': Layers,
  'load-clients': UserCheck,
  'price-lists': Layers,
  'bank-accounts': Landmark,
  'online-store': Globe,
  'payment-methods': CreditCard,
};


const STAGE_DEFINITIONS = {
  1: {
    title: 'Etapa 1: Puesta a punto inicial del negocio',
    badge: 'Operación Base',
    icon: ShieldCheck,
    badgeBg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    description: '¡Al completar estos pasos, tu negocio queda 100% habilitado para cobrar en mostrador, controlar stock e ingresar dinero a tu caja diaria!',
  },
  2: {
    title: 'Etapa 2: Compras, Inventario y Análisis Comercial',
    badge: 'Análisis y Abastecimiento',
    icon: BarChart3,
    badgeBg: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    description: 'Mantené al día la mercadería comprada a proveedores y evaluá en tiempo real el beneficio bruto y los reportes de ventas de tu local.',
  },
  3: {
    title: 'Etapa 3: Crecimiento, Canales y Equipo',
    badge: 'Expansión Avanzada',
    icon: Rocket,
    badgeBg: 'bg-amber-100 text-amber-800 border-amber-300',
    description: 'Aprovechá herramientas avanzadas para vender más: Tienda Web por WhatsApp, Combos, Cuentas Corrientes, permisos de equipo y Facturación ARCA.',
  },
};

export default function ProgresoOnboarding() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const orgId = profile?.organization_id;

  // Cada paso arranca su guia en la ruta que declara el tour (ahi viven sus
  // selectores), no en la del CTA: varias guias empiezan en el formulario y no
  // en el listado.
  const handleStartTour = (step) => {
    const tourId = step.tourId || step.id;
    const tour = getOnboardingTour(tourId);
    if (!tour) {
      // Sin guia registrada no se navega a ningun lado: el boton ni se muestra,
      // pero si algun paso nuevo se cuela, cae en su CTA.
      if (step.cta?.route) navigate(step.cta.route);
      return;
    }
    startSetupTour(tourId);
    // Con `menuPath` la guia enseña el camino: el usuario navega por el menú y
    // el cartel lo acompaña. Sin él, se abre directo la pantalla.
    if (!tour.menuPath) navigate(tour.startRoute || step.cta?.route || '/sistema/progreso');
  };


  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState([]);
  const [setupState, setSetupState] = useState(null);

  const loadData = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const [signals, state, orgRow] = await Promise.all([
        fetchSetupOnboardingSignals(orgId),
        fetchSetupOnboardingState(orgId),
        supabase
          .from('organizations')
          .select('onboarding_v2_enabled, business_type')
          .eq('id', orgId)
          .maybeSingle()
          .then(({ data }) => data || {})
      ]);

      const v2 = orgRow?.onboarding_v2_enabled === true;
      const bizType = v2 ? (orgRow?.business_type || null) : null;
      setSetupState(state);

      const items = buildSetupOnboardingChecklist(signals, state, bizType);
      setChecklist(items);
    } catch (err) {
      console.error('[ProgresoOnboarding] Error:', err);
      toast.error('No pudimos cargar tu progreso.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const progress = useMemo(() => {
    const p = getSetupOnboardingProgress(checklist);
    const total = checklist.length;
    const done = checklist.filter((step) => step.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return {
      ...p,
      total,
      done,
      pct,
    };
  }, [checklist]);

  const groupedChecklist = useMemo(() => {
    const groups = { 1: [], 2: [], 3: [] };
    checklist.forEach((step) => {
      const stage = step.stage || 3;
      if (!groups[stage]) groups[stage] = [];
      groups[stage].push(step);
    });
    return groups;
  }, [checklist]);

  // Sin marcado manual: cada paso se da por hecho cuando el usuario lo hace de
  // verdad (las señales las calcula buildSetupOnboardingChecklist). Dejar un
  // "Ya está" permitía completar la puesta a punto sin haberla hecho.

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#3FF2AA] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Cargando tu progreso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in max-w-4xl mx-auto py-3">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={() => navigate('/sistema/dashboard')}
          className="w-10 h-10 bg-white border border-slate-200 rounded-2xl flex items-center justify-center hover:bg-slate-50 transition-all shadow-sm"
          title="Volver al inicio"
        >
          <ArrowLeft size={16} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase leading-none">Puesta a punto</h1>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">
            Organizado en 3 etapas progresivas para la gestión de tu negocio.
          </p>
        </div>
      </div>

      {/* PROGRESS BANNER */}
      <div className="bg-[#0B2A36] text-white rounded-[1.75rem] p-4 md:p-5 shadow-lg relative overflow-hidden mb-4">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-center md:text-left">

            <h2 className="text-xl md:text-2xl font-black tracking-tight leading-tight">
              {progress.pct === 100 ? '¡Tu negocio está 100% optimizado! 🎉' : 'Paso a paso para operar con éxito.'}
            </h2>
            <p className="mt-1 text-xs text-white/70 max-w-md">
              Al completar la <strong className="text-[#3FF2AA]">Etapa 1</strong> ya podés operar al 100% cobrando en mostrador. Las etapas 2 y 3 potencian tus compras, rentabilidad y ventas.
            </p>
          </div>

          <div data-onboarding-id="progreso-circular-banner" className="flex flex-col items-center shrink-0">
            <div className="relative w-20 h-20 flex items-center justify-center">
              <svg className="absolute w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  className="text-white/5"
                  strokeWidth="8"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                />
                <circle
                  className="text-[#3FF2AA] transition-all duration-500"
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 40}
                  strokeDashoffset={2 * Math.PI * 40 * (1 - progress.pct / 100)}
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                />
              </svg>
              <span className="text-xl font-black">{progress.pct}%</span>
            </div>
            <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-white/60">
              {progress.done} de {progress.total} completados
            </p>
          </div>
        </div>
      </div>

      {/* STAGED STEPS LIST */}
      <div className="space-y-5">
        {[1, 2, 3].map((stageNum) => {
          const stageDef = STAGE_DEFINITIONS[stageNum];
          const stepsInStage = groupedChecklist[stageNum] || [];
          if (stepsInStage.length === 0) return null;

          const StageIcon = stageDef.icon;

          return (
            <div key={stageNum} className="space-y-2">
              {/* STAGE HEADER */}
              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 px-4 py-3 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-slate-800 text-white flex items-center justify-center font-black text-xs shrink-0">
                      {stageNum}
                    </div>
                    <h2 className="text-base md:text-lg font-black text-slate-800 tracking-tight">
                      {stageDef.title}
                    </h2>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${stageDef.badgeBg} w-fit`}>
                    <StageIcon size={12} />
                    {stageDef.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-snug pl-9">
                  {stageDef.description}
                </p>
              </div>

              {/* STEPS IN STAGE */}
              <div className="space-y-1.5 pl-2 md:pl-3">
                {stepsInStage.map((step) => {
                  const StepIcon = STEP_ICONS[step.id] || ShoppingBag;
                  return (
                    <div
                      key={step.id}
                      className={`rounded-2xl border transition-all duration-200 p-3 md:p-3.5 bg-white
                        ${step.done
                          ? 'border-emerald-100 bg-emerald-50/20 opacity-80'
                          : 'border-slate-200 hover:border-[#3FF2AA]/40 hover:shadow-md'}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm
                            ${step.done ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-50 text-slate-400'}`}>
                            {step.done ? <CheckCircle2 size={18} /> : <StepIcon size={18} />}
                          </div>

                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className={`text-sm font-extrabold tracking-tight ${step.done ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                                {step.label}
                              </h3>
                              {step.optional && (
                                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-slate-500">
                                  Opcional
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 leading-snug">{step.description}</p>

                            {/* WHY THIS MATTERS SECTION */}
                            {!step.done && (
                              <p className="text-[11px] text-slate-400 mt-0.5 italic leading-snug">
                                {step.id === 'load-catalog' && '💡 Te permite vender con un clic y automatizar tu stock en tiempo real.'}
                                {step.id === 'first-sale' && '💡 Habilita el control de caja diaria y el historial inteligente de ventas.'}
                                {step.id === 'open-cash' && '💡 Controla el dinero de tus turnos de caja sin sorpresas al cerrar.'}
                                {step.id === 'first-purchase' && '💡 Suma stock automáticamente y mantiene al día la deuda con tus proveedores.'}
                                {step.id === 'business-data' && '💡 Tus tickets saldrán prolijos con tu logo, dirección y CUIT fiscal.'}
                                {step.id === 'payment-methods' && '💡 Sin banco vinculado, la plata de las tarjetas nunca aparece en tu flujo de fondos.'}
                                {step.id === 'load-suppliers' && '💡 Cada compra actualiza sola el costo y la cuenta corriente del proveedor.'}
                                {step.id === 'product-costs' && '💡 Sin costo cargado, tu margen figura en cero en todos los reportes.'}
                                {step.id === 'product-categories' && '💡 Es lo que te deja ver qué rubro deja plata y cuál no.'}
                                {step.id === 'load-clients' && '💡 Habilita la venta en cuenta corriente y el control de fiados.'}
                                {step.id === 'price-lists' && '💡 Precios por canal (mayorista, delivery) sin tocar el precio base.'}
                                {step.id === 'bank-accounts' && '💡 Necesario para conciliar transferencias y ver tu disponible real.'}
                                {step.id === 'online-store' && '💡 Tu catálogo online recibiendo pedidos por WhatsApp con el stock del mostrador.'}
                                {step.id === 'invite-team' && '💡 Sumá a tus colaboradores con permisos específicos y seguros.'}
                                {step.id === 'configure-billing' && '💡 Dejá todo listo para emitir facturas electrónicas ARCA de forma legal.'}
                                {step.id === 'load-recipes' && '💡 Automatiza la baja de insumos por porción cada vez que vendés un plato.'}
                                {step.id === 'publish-web-store' && '💡 Tu catálogo digital online listo para recibir pedidos por WhatsApp.'}
                                {step.id === 'create-first-combo' && '💡 Aumentá el valor promedio de venta ofreciendo promociones y packs.'}
                                {step.id === 'setup-client-credit' && '💡 Llevá el saldo de fiados de forma prolija sin depender de cuadernos.'}
                                {step.id === 'configure-price-lists' && '💡 Manejá precios diferenciados para venta mayorista, mostrador o envíos.'}
                                {step.id === 'setup-treasury-accounts' && '💡 Conciliación clara entre efectivo, Mercado Pago y tus bancos.'}
                                {step.id === 'setup-multi-site-stock' && '💡 Mové mercadería entre depósitos centrales y locales con trazabilidad.'}
                                {step.id === 'review-profitability' && '💡 Medí la ganancia real neta cruzando el costo de tu inventario.'}
                                {step.id === 'explore-reports' && '💡 Visualizá gráficos y tendencias de ventas por hora y categoría.'}
                              </p>
                            )}

                            {/* PROGRESS BAR FOR LOAD-CATALOG */}
                            {!step.done && step.progressTarget && Number(step.progress) > 0 && (
                              <div className="mt-2">
                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 mb-0.5">
                                  <span>Progreso mínimo sugerido</span>
                                  <span className="text-[#3FF2AA]">{step.progress} / {step.progressTarget}</span>
                                </div>
                                <div className="h-1 w-32 overflow-hidden rounded-full bg-slate-100">
                                  <div
                                    className="h-full rounded-full bg-emerald-400"
                                    style={{ width: `${(step.progress / step.progressTarget) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
                          {step.done ? (
                            <>
                              <span className="inline-flex items-center gap-1.5 text-emerald-600 font-extrabold text-[9px] uppercase tracking-wider bg-emerald-100/50 px-2.5 py-1 rounded-full">
                                Listo
                              </span>
                              {getOnboardingTour(step.tourId || step.id) ? (
                                <button
                                  type="button"
                                  onClick={() => handleStartTour(step)}
                                  title="Volver a hacer esta guía"
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:border-[#6d28d9]/40 hover:text-[#6d28d9] transition-all shadow-sm"
                                >
                                  <RotateCcw size={12} />
                                  Repetir
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {getOnboardingTour(step.tourId || step.id) ? (
                              <button
                                type="button"
                                onClick={() => handleStartTour(step)}
                                className="inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-[10px] font-black uppercase tracking-wider hover:scale-105 active:scale-95 transition-all shadow-sm"
                                style={{
                                  background: '#f5f3ff',
                                  borderColor: '#ddd6fe',
                                  color: '#6d28d9',
                                }}
                              >
                                <Sparkles size={12} className="animate-pulse text-[#6d28d9]" />
                                Iniciar guía
                              </button>
                              ) : step.cta?.route ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(step.cta.route)}
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-600 hover:border-[#3FF2AA] hover:text-slate-800 transition-all shadow-sm"
                                >
                                  <ArrowUpRight size={12} />
                                  {step.cta.label || 'Ir'}
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
